/**
 * Web Component
 * Basic HTML editor implementation (replacement to <textarea> tag)
 *
 * Properties:
 * 	disabled - makes it readonly
 * 	clickable - you will be able to click links
 * 	toolbar - name of toolbar set
 */
import {HtmlAreaToolbarManager} from "./HtmlAreaToolbarManager.js";
import * as DebugPanel from "../../utils/DebugPanel.js";

export const TOOLBAR_FULL = "full";
export const TOOLBAR_NO = "no";

export class HtmlAreaElement extends HTMLElement {
	constructor() {
		super();
		this.inputCallback = this.onInput.bind(this);
		this.focusCallback = this.onFocus.bind(this);
		this.blurCallback = this.onBlur.bind(this);
		this.unloadCallback = this.onUnload.bind(this);
		this.dirty = false;
		this.extraTools = [];
	}

	syncEditability() {
		if (this.hasAttribute("disabled")) {
			this.removeAttribute("contenteditable");
			return false;
		} else {
			this.setAttribute("contenteditable", "true");
			return true;
		}
	}

	syncClickability() {
		let clck = this.hasAttribute("clickable");
		this.setClickability(clck);
		if (this.tm) this.tm.setSwitch("clickable", clck);
	}

	setClickability(clickable) {
		this.querySelectorAll("a").forEach((link) => {
			if (clickable) link.setAttribute("contenteditable", "false");	// disable editing = enable clicking
			else link.removeAttribute("contenteditable");	// inherit editability
		})
	}

	onInput(e) {
		this.dirty = true;
		if (this.tm) this.tm.editorMoved();
		/*const s = window.getSelection();
		let info = "C="+s.isCollapsed+" RC="+s.rangeCount;
		if (s.isCollapsed && s.focusNode != null) {
			if (s.focusNode.nodeType === Node.ELEMENT_NODE) info += " <" + s.focusNode.tagName + ">";
			else if (s.focusNode.nodeType === Node.TEXT_NODE) info += " <" + s.focusNode.parentElement.tagName + ">";
		}
		DebugPanel.show(info);*/
	}

	onFocus(e) {
		if (this.tm) {
			this.tm.setSwitch("clickable", this.clickable);
			this.tm.focus(this);
		}
	}

	onBlur(e) {
		if (this.tm) this.tm.blur();
	}

	onUnload(e) {
		console.log("page onload: area " + this.dirty);
		if (this.dirty) {
			this.dispatchEvent(new Event("editstop"));
		}
	}

	set disabled(value) {
		const isDisabled = Boolean(value);
		if (isDisabled) this.setAttribute('disabled', '');
		else this.removeAttribute('disabled');
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set clickable(value) {
		const isClickable = Boolean(value);
		if (isClickable) this.setAttribute('clickable', '');
		else this.removeAttribute('clickable');
	}

	get clickable() {
		return this.hasAttribute('clickable');
	}

	set toolbar(value) {
		const isToolbar = Boolean(value);
		if (isToolbar) this.setAttribute('toolbar', value);
		else this.removeAttribute('toolbar');
	}

	get toolbar() {
		return this.getAttribute('toolbar');
	}

	attributeChangedCallback(name, oldValue, newValue) {
		switch (name) {
			case "disabled":
				this.syncEditability();
				break;
			case "clickable":
				this.syncClickability();
				break;
			case "toolbar":
				if (this.tm) this.tm.changeLayout(newValue);
				break;
		}
	}

	connectedCallback() {
		if (this.syncEditability()) this.syncClickability();
		if (!this.disabled) {
			this.tm = new HtmlAreaToolbarManager(this.getAttribute("toolbar"), this.extraTools);
			this.addEventListener("input", this.inputCallback);
			this.addEventListener("focus", this.focusCallback);
			this.addEventListener("blur", this.blurCallback);
			window.addEventListener("beforeunload", this.unloadCallback);
		}
	}

	disconnectedCallback() {
		if (this.tm) {
			this.tm.uninstall();
			delete this.tm;
		}
		this.removeEventListener("input", this.inputCallback);
		this.removeEventListener("focus", this.focusCallback);
		this.removeEventListener("blur", this.blurCallback);
		window.removeEventListener("beforeunload", this.unloadCallback)
	}

	toolbarAction(command) {
		if (command === "clickable") {
			this.clickable = !this.clickable;
		} else {
			let arg = undefined;
			if (command === "createLink") {
				arg = prompt("Link", "https://");
			}
			document.execCommand(command, false, arg);
		}
		this.dispatchEvent(new CustomEvent("toolbaraction", {
			bubbles:true,
			detail: command
		}));
	}

	/**
	 * Called by toolbar manager when blur operation was finalized (cursor was moved out).
	 * This may be good time to save area content
	 */
	toolbarGone() {
		console.log("Toolbar gone emitting event")
		this.dispatchEvent(new Event("editstop"));
	}

	/**
	 * Return sanitized HTML from this area. All helper/unsafe tags and attributes are removed in the result
	 */
	get safeInnerHTML() {
		const me = this.cloneNode(true);
		me.querySelectorAll("a").forEach((link) => {
			link.removeAttribute("contenteditable");
		})
		return me.innerHTML;	// TODO implement cleanup
	}

	/**
	 * Add extra button to editor's toolbar.
	 * You can implement action on that button by listening to custom event "toolbaraction"
	 * @param name - action name
	 * @param icon - anneca symbol
	 * @param label - optional text label
	 */
	addButton(name, icon, label = null) {
		this.extraTools.push({
			name, icon, label
		});
	}

	addSeparator() {
		this.extraTools.push("");
	}

	static get observedAttributes() {
		return ['disabled', 'clickable', 'toolbar'];
	}
}

customElements.define("html-area", HtmlAreaElement);
